import React, { useEffect, useState } from 'react';
import { camelCase } from 'lodash';
import { useLocation, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getProject, getSampleProject, updateProject } from '@src/apis/project';
import { getRequestV2 } from '@src/apis/requests';
import { Box, CircularProgress, useMediaQuery } from '@mui/material';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import {
  convertBlocksSaveToStore,
  assignEmphasisToBlocks,
  normalizeProjectVideoSettings,
  normalizeNote,
} from '@src/services/project';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { countTextLength } from '@src/services/tts';
import { musicBlocksToMusicClips } from '@src/services/autoSave';
import theme from '@src/styles/theme';
import actions from '@src/redux/actions';
import route from '@src/constants/route';
import { DEFAULT_SYNTHESIS_REQUEST } from '@src/constants/voice';
import {
  CONVERTING_STATUS,
  EXTRACTION_STATUS,
  PROJECT_PRODUCT,
  PROJECT_TYPE,
} from '@src/constants/project';
import { EXPORT_TYPE } from '@src/constants/presentation';
import { MOE_MODULE, MOE_SURFACE } from '@src/constants/moengage';
import { trackAudioPlayed, deriveProjectType } from '@src/services/moengage';
import ProjectOnMobile from '../Project/ProjectOnMobile';

import DubbingDetail from './Dubbing';
import TTS from './TTS';
import PresentationDetail from './Presentation';

const ProjectDetail = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const { getFeatureValue } = useFeatureFlags();
  const { product } = useSelector((state) => state.project);
  const projectSource = useSelector((state) => state.project.source);
  const isPlaying = useSelector((state) => state.project.isPlaying);
  const isPlayingBlock = useSelector((state) => state.project.isPlayingBlock);
  const { languagesV3 } = useSelector((state) => state.language);

  const history = useHistory();
  const location = useLocation();
  const projectId = location.pathname.split('/')[2];

  const [loadingProjectDetail, setLoadingProjectDetail] = useState(true);
  const [extractionState, setExtractionState] = useState(null);

  const user = useSelector((state) => state.auth.user);

  // All three editors drive playback through these flags — one point covers all
  useEffect(() => {
    if ((isPlaying || isPlayingBlock) && projectId) {
      trackAudioPlayed({
        requestId: projectId,
        module: MOE_MODULE.PROJECT,
        surface: isPlayingBlock
          ? MOE_SURFACE.PROJECT_BLOCK
          : MOE_SURFACE.PROJECT_EDITOR,
        projectType: deriveProjectType({ product, source: projectSource }),
      });
    }
  }, [isPlaying, isPlayingBlock, projectId]);

  const blockWithDubbingProjectEnabled = getFeatureValue(
    FEATURE_KEYS.BLOCK_WITH_DUBBING_PROJECT,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const presentationProjectsEnabled = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_PROJECTS,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const fetchProjectDetail = async () => {
    let response;
    const { isSampleProject } = location.state || {};
    dispatch(actions.project.setIsLoadingProjectDetail(true));

    if (isSampleProject) {
      const sampleProject = await getSampleProject();
      const {
        blocks = [],
        title,
        audioType,
      } = sampleProject?.result?.sampleProject || {};
      await updateProject(projectId, {
        title,
        blocks: blocks.map((block) => ({
          id: block.id,
          title: block.title,
          characters: countTextLength(block.text || ''),
          text: block.text || '',
          currentNote: normalizeNote(block.currentNote) || undefined,
          voiceCode: block.voiceCode,
          breakTime: block.breakTime,
          speed: block.speed,
          elements: block.elements,
          isSample: true,
          startTime: block?.startTime || undefined,
          endTime: block?.endTime || undefined,
        })),
        audioType,
      });
      response = sampleProject?.result?.sampleProject;
    } else {
      const detailProject = await getProject(projectId);
      response = detailProject?.result?.project;
    }
    if (response) {
      const {
        blocks = [],
        title,
        createdAt,
        product: projectProduct,
        audioType,
        audioLink,
        audioExpiredAt,
        status,
        requestId,
        translation,
        clientPause,
        pronunciationDict,
        source,
        exports,
        videoSettings,
        captionTemplate,
        projectCaptionTemplate,
        musicBlocks,
        volumes,
      } = response || {};
      const hasProductPending = [
        PROJECT_PRODUCT.PRESENTATION,
        PROJECT_PRODUCT.DUBBING,
      ].includes(projectProduct);

      if (hasProductPending && source?.extraction?.response?.status) {
        setExtractionState(source?.extraction?.response?.status);
      }
      // API responses are deep camelCased (see apis/api.js), which camelCases
      // the EXPORT_TYPE keys (PPTX -> pptx, JOIN_AUDIO -> joinAudio). Re-map
      // back to EXPORT_TYPE keys. Legacy `exports.audio` docs are dropped
      // (no matching key), so the user re-exports under the new flow.
      //
      // Dispatched for EVERY product, not just the ones that can export: the
      // store is never reset between projects, so skipping it left the previous
      // project's entries in place. A PENDING export on the project you came
      // from then reads as "this project is downloading" and locks the editor
      // here. `null` for a project with no exports is the clear.
      const normalizedExports = exports
        ? Object.values(EXPORT_TYPE).reduce((acc, type) => {
            const exportData = exports[camelCase(type)];
            if (exportData) acc[type] = exportData;
            return acc;
          }, {})
        : null;
      dispatch(actions.project.setExports(normalizedExports));
      const modifiedBlocks = assignEmphasisToBlocks(blocks);
      const multiBlock = convertBlocksSaveToStore(modifiedBlocks);
      // Set product + audioType + videoSettings before multiBlock so the
      // SET_MULTI_BLOCK reducer computes blockStatus with the correct context
      // AND captures the right auto-save baseline (title/audioType/videoSettings).
      dispatch(actions.project.setProduct(projectProduct));
      dispatch(actions.project.setAudioType(audioType, { skipAutoSave: true }));
      dispatch(actions.project.setOriginalAudioType(audioType));
      dispatch(actions.project.setTitle(title));
      dispatch(actions.project.setOriginalTitle(title));
      // Per-track volumes to restore (0..100), defaulting to full so they don't
      // leak from a previously opened project. Null for non-video-dubbing.
      let restoredVolumes = null;
      if (
        projectProduct === PROJECT_PRODUCT.DUBBING &&
        source?.type === PROJECT_TYPE.VIDEO
      ) {
        const normalizedVideoSettings = normalizeProjectVideoSettings(
          videoSettings,
          captionTemplate,
          projectCaptionTemplate,
        );
        dispatch(actions.project.setVideoSettings(normalizedVideoSettings));
        dispatch(
          actions.project.setOriginalVideoSettings(normalizedVideoSettings),
        );
        // Restore per-track volumes (stored 0..100 → player 0..1). skipAutoSave
        // so opening doesn't schedule a save; the baseline is seeded below.
        restoredVolumes = {
          voiceover: volumes?.voiceover ?? 100,
          media: volumes?.media ?? 100,
          music: volumes?.music ?? 100,
        };
        dispatch(
          actions.project.setTrackVolumes({
            voiceover: restoredVolumes.voiceover / 100,
            media: restoredVolumes.media / 100,
            music: restoredVolumes.music / 100,
          }),
        );
      }
      // Always set from the loaded project (empty when none) so music clips
      // don't leak from a previously opened project. Before setMultiBlock so the
      // auto-save baseline captures them.
      dispatch(
        actions.project.setMusicClips(musicBlocksToMusicClips(musicBlocks)),
      );
      // Volumes are set above, so setMultiBlock captures them into the auto-save
      // baseline along with the blocks — opening a project re-saves nothing.
      dispatch(actions.project.setMultiBlock(multiBlock));
      dispatch(actions.project.setCreatedAt(createdAt));
      dispatch(actions.project.setProjectId(projectId));
      dispatch(actions.project.setAudioLinkSrt(audioLink));
      dispatch(actions.project.setAudioSrtExpiredAt(audioExpiredAt));
      dispatch(actions.project.setStatus(status));
      dispatch(actions.project.setSource(source || null));
      if (
        projectProduct === PROJECT_PRODUCT.PRESENTATION &&
        source?.generation?.response?.status === EXTRACTION_STATUS.PENDING
      ) {
        const generateScriptBlockIds =
          source?.generation?.response?.metadata?.generateScriptBlockIds || [];
        dispatch(
          actions.project.setIsGeneratingNarration(
            true,
            generateScriptBlockIds,
          ),
        );
      }
      dispatch(actions.project.unselectAllBlocks());
      dispatch(actions.project.setTranslation(translation || null));
      dispatch(
        actions.project.setSynthesisParams({ clientPause, pronunciationDict }),
      );
      dispatch(actions.project.setDubbingRequestId(requestId));
      setLoadingProjectDetail(false);

      const isProjectPending =
        source?.extraction?.response?.status === EXTRACTION_STATUS.PENDING;
      if (hasProductPending && !isProjectPending) {
        dispatch(actions.project.setIsLoadingProjectDetail(false));
      }
      // Reset state when user navigate out of project detail
      // so that when user navigate to project detail again
      // the audio won't automatically play.
      dispatch(actions.project.setCurrentBlockId(''));
      dispatch(actions.project.setCurrentPlayingBlockId(''));
      dispatch(actions.project.setIsPlayingBlock(false));
      dispatch(actions.project.setIsPlaying(false));
      dispatch(
        actions.project.setIsProjectSynthesizing(
          status === CONVERTING_STATUS.IN_PROGRESS,
        ),
      );
      if (requestId) {
        const requestDetail = await getRequestV2(requestId);
        const { result } = requestDetail;
        dispatch(actions.synthesisRequest.updateSynthesisConfig(result));
      } else {
        dispatch(
          actions.synthesisRequest.updateSynthesisConfig(
            DEFAULT_SYNTHESIS_REQUEST,
          ),
        );
      }
    } else {
      history.replace(route.PROJECTS);
    }
  };
  const fetchSourceProjectDetail = async () => {
    const detailProject = await getProject(projectId, 'source');
    const response = detailProject?.result?.project;
    if (response) {
      const { source } = response || {};
      dispatch(actions.project.setSource(source || null));
    }
  };

  useEffect(() => {
    fetchProjectDetail();
    // Fetch languages early for translation dropdown
    if (languagesV3.length === 0) {
      dispatch(actions.language.getLanguagesV3());
    }
  }, []);

  if (isMobile) {
    return (
      <div style={{ padding: '0 16px' }}>
        <ProjectOnMobile />
      </div>
    );
  }

  if (loadingProjectDetail) {
    return (
      <Box
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (product === PROJECT_PRODUCT.DUBBING && blockWithDubbingProjectEnabled) {
    return (
      <DubbingDetail
        extractionStatus={extractionState}
        onExtractionComplete={() => {
          setExtractionState(EXTRACTION_STATUS.SUCCESS);
          dispatch(actions.project.setIsLoadingProjectDetail(false));
          fetchProjectDetail();
        }}
        projectId={projectId}
      />
    );
  }
  if (product === PROJECT_PRODUCT.PRESENTATION && presentationProjectsEnabled) {
    return (
      <PresentationDetail
        extractionStatus={extractionState}
        onExtractionComplete={() => {
          setExtractionState(EXTRACTION_STATUS.SUCCESS);
          dispatch(actions.project.setIsLoadingProjectDetail(false));
          fetchProjectDetail();
        }}
        onGenerationComplete={() => {
          fetchSourceProjectDetail();
        }}
        projectId={projectId}
      />
    );
  }
  return <TTS />;
};

export default ProjectDetail;
